import React, { useEffect, useMemo, useState } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/SchoolFees/PageTitle";
import ajaxSchool from "../../util/remote/ajaxSchool";
import PaymentForm from "../../components/SchoolFees/PaymentForm";
import { useTheme } from "../../DataStore/ThemeContext";

const safe = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const StudentStat = ({ label, value, icon, tone = "primary" }) => (
  <div className="col-sm-6 col-lg-4 col-xl-2 mb-3">
    <div
      className="bg-white"
      style={{
        borderRadius: '12px',
        padding: '14px',
        border: '1px solid #F1F5F9',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <div className="d-flex align-items-center mb-1">
        <i className={`${icon} mr-2`} style={{ fontSize: '12px', color: '#94A3B8' }} />
        <span className="text-muted text-uppercase font-weight-bold" style={{ fontSize: '10px', letterSpacing: '0.03em' }}>
          {label}
        </span>
      </div>
      <div
        className="font-weight-bold mt-1"
        style={{ fontSize: '13px', color: '#1E293B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        title={String(value ?? "")}
      >
        {safe(value)}
      </div>
    </div>
  </div>
);

const PaySchoolFeesPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const { openSettings } = useTheme();
  const [students, setStudents] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const term = (searchTerm || "").trim();
    if (term.length > 2) {
      const t = setTimeout(() => searchStudent(term), 250);
      return () => clearTimeout(t);
    }
    setStudents(null);
  }, [searchTerm]);

  const searchStudent = async (term) => {
    setSelectedStudent(null);
    setIsSearching(true);

    const server_response = await ajaxSchool.searchStudent(term);
    if (server_response.status === "OK") setStudents(server_response.details);
    else setStudents("404");

    setIsSearching(false);
  };

  const handleBilling = () => {
    if (!selectedStudent) return;
    openSettings("Bill Selection", "BillingPreview", [selectedStudent.id], "Proceed to Bill");
  };

  const handleSelection = (student) => {
    setSelectedStudent(student);
    setStudents(null);
    setSearchTerm("");
  };

  const headerMeta = useMemo(() => {
    if (!selectedStudent) return null;
    const billed = Number(selectedStudent.current_bill) > 0;
    const current = Number(selectedStudent.status) > 0;

    return (
      <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
        {safe(selectedStudent.reg_no)}{" "}
        <span className="mx-2">•</span>
        {safe(selectedStudent.class?.class_name || "N/A")}
        <span className="mx-2">•</span>
        <span className={billed ? "text-success" : "text-warning"}>
          <i className={`fa ${billed ? "fa-circle-check" : "fa-circle-exclamation"} mr-1`} />
          {billed ? "Billed" : "Not billed"}
        </span>
        <span className="mx-2">•</span>
        <span className="text-muted">
          <i className={`fa ${current ? "fa-user-check" : "fa-user-slash"} mr-1`} />
          {current ? "Current" : "Former"}
        </span>
      </div>
    );
  }, [selectedStudent]);

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="New Payment"
          description="Record new fees payment for students. All payments are entered according to fees votes."
          iconClass="fa fa-handshake"
          sticky={true}
        />

        {/* Search Section */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h6 className="font-weight-bold mb-1" style={{ color: '#1E293B' }}>Find Student</h6>
                    <p className="text-muted small mb-0">Enter a name or registration number to begin payment.</p>
                </div>
                {!selectedStudent && (
                    <span className="badge-soft-secondary py-1 px-3">
                        <i className="fa-solid fa-circle-info mr-1" /> Select student
                    </span>
                )}
            </div>

            <div className="position-relative">
              <div className="input-group" style={{ 
                  borderRadius: '12px', overflow: 'hidden', border: '1px solid #E2E8F0',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}>
                <span className="input-group-text bg-white border-0 text-muted px-3">
                  <i className="fa-solid fa-magnifying-glass" />
                </span>
                <input
                  type="text"
                  className="form-control border-0 shadow-none py-4"
                  placeholder="Enter student name or registration number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ fontSize: '15px' }}
                />
              </div>

              {isSearching && (
                <div className="position-absolute" style={{ right: '15px', top: '12px', zIndex: 5 }}>
                    <div className="spinner-border spinner-border-sm text-indigo" role="status" />
                </div>
              )}

             {/* Improved Search Results Container */}
                {Array.isArray(students) && students.length > 0 && (
                  <div
                    className="bg-white shadow-lg border-0 mt-2"
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      top: "100%",
                      borderRadius: "12px",
                      zIndex: 1000,
                      overflow: "hidden", // Ensures children don't break rounded corners
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
                    }}
                  >
                    {/* Header for context */}
                    <div className="px-3 py-2 bg-light border-bottom d-flex justify-content-between align-items-center">
                        <span className="text-muted font-weight-bold" style={{ fontSize: '10px', uppercase: true, letterSpacing: '0.05em' }}>
                            {students.length} STUDENTS FOUND
                        </span>
                    </div>

                    {/* Scrollable Area */}
                    <div style={{ maxHeight: "350px", overflowY: "auto", scrollbarWidth: 'thin' }}>
                      {students.map((student) => (
                        <button
                          key={student.id}
                          type="button"
                          className="btn btn-link text-left w-100 student-result-item"
                          onClick={() => handleSelection(student)}
                          style={{
                            textDecoration: "none",
                            padding: "12px 16px",
                            borderBottom: "1px solid #F1F5F9",
                            transition: "all 0.2s ease",
                            display: "block"
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <div style={{ minWidth: 0 }}>
                              <div className="font-weight-bold mb-0 text-dark" style={{ fontSize: "14px" }}>
                                {student.full_name}
                              </div>
                              <div className="text-muted d-flex align-items-center mt-1" style={{ fontSize: "12px" }}>
                                <span className="badge-soft-secondary mr-2">{student.reg_no}</span>
                                <span className="opacity-50">•</span>
                                <span className="ml-2">{student.class?.class_name || "No Class Assigned"}</span>
                              </div>
                            </div>
                            <i className="fa-solid fa-chevron-right text-muted" style={{ fontSize: '10px' }} />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Empty state */}
        {!selectedStudent && (
          <div className="text-center p-5 bg-white shadow-sm border-0" style={{ borderRadius: '16px' }}>
            <div className="mb-3">
              <span className="rounded-circle d-inline-flex align-items-center justify-content-center" 
                    style={{ width: 64, height: 64, background: '#F5F7FF', color: '#4F46E5' }}>
                <i className="fa fa-magnifying-glass fa-xl" />
              </span>
            </div>
            <h6 className="font-weight-bold" style={{ color: '#1E293B' }}>Find a student to start</h6>
            <p className="text-muted small mx-auto mb-4" style={{ maxWidth: '320px' }}>
              Search and select a student above to record a new fees payment or manage their billing.
            </p>
            <div className="badge-soft-indigo py-2 px-4 d-inline-block">
                <i className="fa-solid fa-bolt mr-2" />
                <strong>TIP:</strong> Search by <strong>Reg No</strong> for fastest results
            </div>
          </div>
        )}

        {/* Selected student area */}
        {selectedStudent && (
          <>
            <div
              className="card shadow-sm mb-4"
              style={{ borderRadius: 18, border: "1px solid rgba(0,0,0,.05)" }}
            >
              <div className="card-body">
                <div className="d-flex align-items-start justify-content-between flex-wrap">
                  <div>
                    <div className="font-weight-semibold" style={{ fontSize: 15 }}>
                      Payment for {selectedStudent.full_name}
                    </div>
                    {headerMeta}
                  </div>

                  {Number(selectedStudent.current_bill) === 0 && (
                    <button
                      className="btn btn-outline-primary"
                      onClick={handleBilling}
                      style={{ borderRadius: 12 }}
                    >
                      <i className="fa fa-money-bill mr-2" />
                      Bill student
                    </button>
                  )}
                </div>

                <hr />

                <div className="row">
                  <StudentStat label="Student name" value={selectedStudent.full_name} icon="fa fa-user" tone="primary" />
                  <StudentStat label="Gender" value={safe(selectedStudent.gender)} icon="fa fa-venus-mars" tone="info" />
                  <StudentStat label="Class" value={safe(selectedStudent.class?.class_name || "N/A")} icon="fa fa-school" tone="success" />
                  <StudentStat label="Reg no" value={safe(selectedStudent.reg_no)} icon="fa fa-id-card" tone="warning" />
                  <StudentStat
                    label="Billing status"
                    value={Number(selectedStudent.current_bill) > 0 ? "BILLED" : "NOT BILLED"}
                    icon="fa fa-file-invoice"
                    tone={Number(selectedStudent.current_bill) > 0 ? "success" : "warning"}
                  />
                  <StudentStat
                    label="Student status"
                    value={Number(selectedStudent.status) > 0 ? "Current Student" : "Former Student"}
                    icon="fa fa-user-check"
                    tone={Number(selectedStudent.status) > 0 ? "info" : "secondary"}
                  />
                </div>
              </div>
            </div>

            <PaymentForm studentId={selectedStudent.id} />
          </>
        )}
      </MainContent>
    </PageContainer>
  );
};

export default PaySchoolFeesPage;
